import {
  Eye,
  FileClock,
  Fingerprint,
  Globe2,
  MessageSquare,
  Plug,
  PowerOff,
  SlidersHorizontal,
} from 'lucide-react';

import {
  Band,
  CtaArrow,
  ctaClasses,
  Lead,
  Medallion,
  MonoMeta,
  Panel,
  Pill,
  SectionTitle,
} from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero, Reveal, SectionHeader } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { EmbedSnippet } from './EmbedSnippet';
import { CONTROL_FACTS, HOW_STEPS } from './steps-data';

import type { ControlFactId } from './steps-data';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

/**
 * Icons for the control grid, keyed by fact id.
 *
 * They live here rather than in `steps-data.ts` because that module is the
 * page's DATA and this is presentation — and because hanging a `LucideIcon` on
 * every entry there would make the data file import the icon set for no gain.
 */
const CONTROL_ICON: Record<ControlFactId, LucideIcon> = {
  identity: Fingerprint,
  confirm: Eye,
  audit: FileClock,
  scope: SlidersHorizontal,
  residency: Globe2,
  killSwitch: PowerOff,
};

/**
 * `/how-it-works` — the page the top bar's second link points at.
 *
 * ## Who it is for
 *
 * Somebody who has read the landing page, believes the demo, and now wants the
 * mechanism: what do I actually do, what happens without me, and what is it
 * allowed to do once it is running. They are not a developer — the page names
 * no SDK, no framework and no API, and the one code block on it is the four
 * lines a customer pastes, shown because seeing it is what makes "one line"
 * credible rather than because anyone has to understand it.
 *
 * ## The shape
 *
 * Three steps, then the three questions the third step raises — what can it
 * DO, what stops it, and where does it show up. The control grid is
 * deliberately the largest block on the page: for this buyer the interesting
 * question is never "can it" but "can I let it".
 *
 * Every visitor-facing string resolves through `useT()` under `howItWorks.*`.
 */
export function HowItWorks(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('howItWorks.meta.title'),
    description: t('howItWorks.meta.description'),
    path: '/how-it-works',
  });

  return (
    <main id="main" tabIndex={-1} className="bg-[color:var(--bd-page)]">
      <PageHero
        eyebrow={t('howItWorks.eyebrow')}
        title={t('howItWorks.title')}
        description={t('howItWorks.lead')}
      />

      {/* ── The three steps ──────────────────────────────────────────────
          A vertical stack rather than three columns, and that is a reading
          decision rather than a layout one: the steps are SEQUENTIAL, and
          three side-by-side cards say "pick one". The rule down the inline
          start is the sequence made visible; it is `aria-hidden` because the
          ordered list already carries the order for anyone not looking at it. */}
      <Band as="section" tone="page" space="lg" aria-labelledby="steps-heading">
        <SectionHeader
          id="steps-heading"
          eyebrow={t('howItWorks.steps.eyebrow')}
          sub={t('howItWorks.steps.sub')}
        >
          {t('howItWorks.steps.heading')}
        </SectionHeader>

        <ol className="relative mt-12 flex flex-col gap-6">
          {HOW_STEPS.map((step, index) => (
            <Reveal as="li" key={step.id} delayMs={index * 90}>
              <Panel
                as="section"
                aria-labelledby={`step-${step.id}`}
                className="grid gap-6 p-7 sm:p-9 md:grid-cols-[auto_1fr] md:gap-8"
              >
                <div className="flex items-center gap-4 md:flex-col md:items-start md:gap-3">
                  <Medallion icon={step.icon} hue={step.hue} />
                  {/* The step number is decoration over an <ol>: the list
                      already numbers itself for assistive tech, so rendering
                      it again would say "one" twice.

                      `--bd-subtle` rather than a line token. It is decoration,
                      but it is decoration made of TEXT, and the rule
                      `line-tokens-are-not-text.test.ts` enforces is exactly
                      this: `--bd-line` is 1.4:1 furniture, so a 2.6rem numeral
                      painted with it is a numeral nobody can read — which is
                      not restraint, it is an element that does not render. */}
                  <span
                    aria-hidden="true"
                    className="font-mono text-[2.6rem] font-semibold leading-none tracking-[-0.05em] text-[color:var(--bd-subtle)] opacity-40"
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <h3
                      id={`step-${step.id}`}
                      className="text-[1.4rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
                    >
                      {t(`howItWorks.step.${step.id}.title`)}
                    </h3>
                    <MonoMeta>{t(`howItWorks.step.${step.id}.time`)}</MonoMeta>
                  </div>

                  <p className="mt-3 max-w-[62ch] text-[1.02rem] leading-[1.68] text-[color:var(--bd-body)]">
                    {t(`howItWorks.step.${step.id}.body`)}
                  </p>

                  <ul className="mt-5 flex flex-col gap-2.5">
                    {Array.from({ length: step.detailCount }, (_, i) => (
                      <li
                        key={i}
                        className="flex gap-3 text-[0.95rem] leading-[1.6] text-[color:var(--bd-muted)]"
                      >
                        <span
                          aria-hidden="true"
                          className="mt-[0.6em] size-[5px] flex-none rounded-full bg-[color:var(--bd-blue)]"
                        />
                        <span>{t(`howItWorks.step.${step.id}.detail.${i + 1}`)}</span>
                      </li>
                    ))}
                  </ul>

                  {step.id === 'install' ? <EmbedSnippet /> : null}
                </div>
              </Panel>
            </Reveal>
          ))}
        </ol>
      </Band>

      {/* ── What it can do once it is live ─────────────────────────────── */}
      <Band as="section" tone="sunk" space="lg" aria-labelledby="does-heading">
        <SectionHeader
          id="does-heading"
          eyebrow={t('howItWorks.does.eyebrow')}
          sub={t('howItWorks.does.sub')}
        >
          {t('howItWorks.does.heading')}
        </SectionHeader>

        <div className="mt-11 grid grid-cols-1 gap-4 md:grid-cols-3">
          {['answer', 'act', 'guide'].map((id, i) => (
            <Reveal key={id} delayMs={i * 80}>
              <Panel className="flex h-full flex-col gap-3 p-7">
                <Pill>{t(`howItWorks.does.${id}.tag`)}</Pill>
                <h3 className="text-[1.12rem] font-semibold leading-[1.2] tracking-[-0.02em] text-[color:var(--bd-ink)]">
                  {t(`howItWorks.does.${id}.title`)}
                </h3>
                <p className="text-[0.95rem] leading-[1.62] text-[color:var(--bd-muted)]">
                  {t(`howItWorks.does.${id}.body`)}
                </p>
                <p className="mt-auto pt-3 text-[0.9rem] italic leading-[1.55] text-[color:var(--bd-body)]">
                  {t(`howItWorks.does.${id}.example`)}
                </p>
              </Panel>
            </Reveal>
          ))}
        </div>
      </Band>

      {/* ── What stays under your control ──────────────────────────────── */}
      <Band as="section" tone="band" space="lg" ambient aria-labelledby="control-heading">
        <SectionHeader
          id="control-heading"
          eyebrow={t('howItWorks.control.eyebrow')}
          sub={t('howItWorks.control.sub')}
          onDark
        >
          {t('howItWorks.control.heading')}
        </SectionHeader>

        <div className="mt-11 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CONTROL_FACTS.map((fact, i) => (
            <Reveal key={fact.id} delayMs={i * 70}>
              <Panel onDark className="flex h-full flex-col gap-3 p-6">
                <Medallion icon={CONTROL_ICON[fact.id]} hue={fact.hue} onDark size="sm" />
                <h3 className="text-[1.05rem] font-semibold leading-[1.22] tracking-[-0.02em] text-white">
                  {t(`howItWorks.control.${fact.id}.title`)}
                </h3>
                <p className="text-[0.93rem] leading-[1.62] text-[color:var(--bd-band-body)]">
                  {t(`howItWorks.control.${fact.id}.body`)}
                </p>
              </Panel>
            </Reveal>
          ))}
        </div>
      </Band>

      {/* ── Where it appears ───────────────────────────────────────────── */}
      <Band as="section" tone="page" space="lg" aria-labelledby="surfaces-heading">
        <SectionHeader
          id="surfaces-heading"
          eyebrow={t('howItWorks.surfaces.eyebrow')}
          sub={t('howItWorks.surfaces.sub')}
        >
          {t('howItWorks.surfaces.heading')}
        </SectionHeader>

        <div className="mt-11 grid grid-cols-1 gap-4 md:grid-cols-2">
          {(
            [
              ['widget', MessageSquare, 'cyan'],
              ['mcp', Plug, 'violet'],
            ] as const
          ).map(([id, icon, hue], i) => (
            <Reveal key={id} delayMs={i * 90}>
              <Panel className="flex h-full flex-col gap-4 p-7 sm:p-8">
                <Medallion icon={icon} hue={hue} />
                <h3 className="text-[1.24rem] font-semibold leading-[1.18] tracking-[-0.028em] text-[color:var(--bd-ink)]">
                  {t(`howItWorks.surfaces.${id}.title`)}
                </h3>
                <p className="text-[0.98rem] leading-[1.65] text-[color:var(--bd-body)]">
                  {t(`howItWorks.surfaces.${id}.body`)}
                </p>
                <p className="mt-auto pt-2 text-[0.9rem] leading-[1.55] text-[color:var(--bd-muted)]">
                  {t(`howItWorks.surfaces.${id}.note`)}
                </p>
              </Panel>
            </Reveal>
          ))}
        </div>
      </Band>

      {/* ── Closing ────────────────────────────────────────────────────── */}
      <Band as="section" tone="band" space="md" aria-label={t('howItWorks.closing.label')}>
        <Container width="prose" className="text-center">
          <SectionTitle onDark className="mx-auto max-w-2xl text-[clamp(1.5rem,2.6vw,2rem)]">
            {t('howItWorks.closing.heading')}
          </SectionTitle>
          <Lead onDark className="mx-auto mt-4 max-w-xl">
            {t('howItWorks.closing.body')}
          </Lead>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/contact" className={ctaClasses({ onDark: true })}>
              {t('howItWorks.closing.primary')}
              <CtaArrow />
            </Link>
            <Link to="/features" className={ctaClasses({ variant: 'secondary', onDark: true })}>
              {t('howItWorks.closing.secondary')}
            </Link>
          </div>
        </Container>
      </Band>
    </main>
  );
}
